import { InMemoryWebStorage, UserManager, WebStorageStateStore } from 'oidc-client-ts'
import { OIDC_API_CLIENT_ID, OIDC_AUTHORITY } from '../endpoints.constants'

export const API_TOKEN_CALLBACK_PATH = '/api-token-callback'

const assertApiClientConfigured = (): string => {
    if (!OIDC_API_CLIENT_ID) {
        throw new Error('VITE_OIDC_API_CLIENT_ID must be set to enable API token issuance')
    }
    return OIDC_API_CLIENT_ID
}

// Keep the long-lived offline refresh_token out of sessionStorage entirely.
const apiTokenUserStore = new WebStorageStateStore({ store: new InMemoryWebStorage() })

// Lazy: avoid throwing at module load time so an unset env doesn't crash
// the whole ProfilePage — error surfaces only when the user actually
// clicks "Create token".
let _apiTokenUserManager: UserManager | null = null
const getApiTokenUserManager = (): UserManager => {
    if (_apiTokenUserManager) return _apiTokenUserManager
    const clientId = assertApiClientConfigured()
    _apiTokenUserManager = new UserManager({
        authority: OIDC_AUTHORITY,
        client_id: clientId,
        redirect_uri: `${window.location.origin}${API_TOKEN_CALLBACK_PATH}`,
        scope: 'openid offline_access',
        response_type: 'code',
        userStore: apiTokenUserStore,
        monitorSession: false,
        automaticSilentRenew: false,
    })
    return _apiTokenUserManager
}

export class OfflineTokenSessionExpiredError extends Error {
    constructor() {
        super('Keycloak SSO session expired')
        this.name = 'OfflineTokenSessionExpiredError'
    }
}

export const requestOfflineToken = async (): Promise<string> => {
    try {
        // Silent (hidden iframe, prompt=none) — reuses the existing Keycloak SSO
        // session, so no visible popup. Returns null if there's no active session.
        const user = await getApiTokenUserManager().signinSilent()
        const token = user?.refresh_token
        if (!token) {
            throw new OfflineTokenSessionExpiredError()
        }
        return token
    } catch (error) {
        // prompt=none with an expired/absent SSO session rejects with
        // login_required / interaction_required.
        const oidcError = (error as { error?: string; error_description?: string })?.error
        const errorDescription = (error as { error_description?: string })?.error_description
        if (oidcError === 'login_required' || oidcError === 'interaction_required') {
            throw new OfflineTokenSessionExpiredError()
        }
        // Non-OIDC failure (network / CORS / iframe timeout). At this point KC
        // may have minted an offline session whose token never reached us, so
        // we can't revokeOfflineToken it — log enough context to find such
        // orphans manually via admin API if they show up in support.
        console.error('API token silent signin failed (non-OIDC)', {
            oidcError,
            errorDescription,
            error,
        })
        throw error
    } finally {
        // removeUser() only clears the in-memory user store. We deliberately
        // do NOT revokeTokens() / signoutSilent() here: the offline refresh_token
        // was just handed to the dialog and must stay valid until either the
        // register call succeeds (then it lives at Keycloak as the API token)
        // or it fails (then the dialog calls revokeOfflineToken explicitly).
        try {
            await getApiTokenUserManager().removeUser()
        } catch (error) {
            console.error('Failed to clear in-memory offline token user', error)
        }
    }
}

export const requestOfflineTokenPopup = async (): Promise<string> => {
    try {
        // Visible popup — for the case when silent failed (e.g. KC needs
        // a one-time consent for offline_access on this client). KC will
        // either show the consent screen or, if the SSO cookie is missing,
        // a login screen. Either way the user completes it explicitly.
        const user = await getApiTokenUserManager().signinPopup()
        const token = user?.refresh_token
        if (!token) {
            throw new OfflineTokenSessionExpiredError()
        }
        return token
    } finally {
        try {
            await getApiTokenUserManager().removeUser()
        } catch (error) {
            console.error('Failed to clear in-memory offline token user', error)
        }
    }
}

export const revokeOfflineToken = async (token: string): Promise<void> => {
    const clientId = assertApiClientConfigured()
    const url = `${OIDC_AUTHORITY}/protocol/openid-connect/revoke`
    const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
            client_id: clientId,
            token,
            token_type_hint: 'refresh_token',
        }),
    })
    if (!res.ok) {
        throw new Error(`Keycloak revoke failed: ${res.status}`)
    }
}

export const handleOfflineTokenCallback = (): Promise<void> => {
    // Popups have window.opener set to the parent; silent iframes don't.
    // Use that to pick the right oidc-client-ts callback so both flows work
    // from the same route.
    const isPopup = typeof window !== 'undefined' && !!window.opener && window.opener !== window
    return isPopup
        ? getApiTokenUserManager().signinPopupCallback()
        : getApiTokenUserManager().signinSilentCallback()
}
